import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ArrowLeftIconComponent,
  ChevronDownIconComponent,
  TagRightIconComponent,
  ArrowRightIconComponent,
  TimerIconComponent,
} from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { PreviousRouteService } from '../../services/previous-route/previous-route.service';
import { PermissionsDirective } from '../../../../ui-components/directives/permissions/permissions.directive';
import { AssignGroupUsersComponent } from '../assign-group-users/assign-group-users.component';
import { CollaboratorSectionComponent } from './collaborator-section/collaborator-section.component';
import { BadgeComponent } from '../../../../ui-components/lib/badge/badge.component';
import { CopyLinkComponent } from '../../../../ui-components/lib/copy-link/copy-link.component';
import { ModalServiceService } from '../../../../ui-components/lib/modal/modal-service.service';
import { DeleteConfirmationPopupComponent } from '../../../common/components/delete-confirmation-popup/delete-confirmation-popup.component';
import { LookupService } from '../../../common/services/lookup/lookup.service';
import { Option } from '../../../common/models/option.model';
import { CollaboratorService } from '../../../pages/employee-flow/request/services/collaborator/collaborator.service';
import { RequestDetailsResponse } from '../../../common/models/request-details.model';
import { RequestTypeEnum } from '../../../common/enums/request-type';
import { StepStatusPipe } from '../../../common/pipes/step-status/step-status.pipe';
import { Meta } from '../../../common/models/api-response.model';
import { AppPermissions } from '../../../common/constants/app-permissions';
import { FeatureFlagsService } from '../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../common/constants/feature-flags.constants';
import { TenantService } from '../../../common/services/tenant/tenant.service';
import { EMPLOYEE_ROUTES } from '../../../common/constants/employee.routing.constants';
import { RequestService } from '../../services/request/request.service';

@Component({
  selector: 'lib-request-details-header',
  imports: [
    BadgeComponent,
    StepStatusPipe,
    TranslocoPipe,
    PermissionsDirective,
    ChevronDownIconComponent,
    AssignGroupUsersComponent,
    CollaboratorSectionComponent,
    DeleteConfirmationPopupComponent,
    ArrowLeftIconComponent,
    CopyLinkComponent,
    NgTemplateOutlet,
    TagRightIconComponent,
    ArrowRightIconComponent,
    TimerIconComponent,
  ],
  templateUrl: './request-details-header.component.html',
  styleUrl: './request-details-header.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestDetailsHeaderComponent {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  modalService = inject(ModalServiceService);
  lookupService = inject(LookupService);
  collaboratorService = inject(CollaboratorService);
  tenantService = inject(TenantService);
  private readonly requestService = inject(RequestService);
  featureFlagsService = inject(FeatureFlagsService);
  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );
  previousRouteService = inject(PreviousRouteService);
  private readonly destroyRef = inject(DestroyRef);

  assignCollaboratorsDialog = viewChild<TemplateRef<unknown>>(
    'assignCollaboratorsDialog'
  );
  deleteCollaboratorDialog = viewChild<TemplateRef<unknown>>(
    'deleteCollaboratorDialog'
  );

  requestDetailsResponse = input.required<RequestDetailsResponse>();
  showTrigger = input<boolean>(true);
  enableTriggerDropdown = input<boolean>(false);
  showStatus = input<boolean>(false);
  showRequestId = input<boolean>(false);
  showDescription = input<boolean>(true);
  showCategory = input<boolean>(true);
  disableExpand = input<boolean>(false);
  requestType = input<string>(RequestTypeEnum.VIEW);
  assignedList = input<Option[]>([]); // Assigned users (Card List)
  rawCollaborators = input<Option[]>([]);
  assignedListCurrentPage = input<number>(1);
  assignedListTotalPages = input<number>(1);
  assignedListTotal = input<number>(0);
  isAssignedListLoading = input<boolean>(false);
  showCollaboratorsSection = input<boolean>(false);
  showAddScheduleAction = input<boolean>(false);
  discardChanges = output();
  getAssignedCardList = output<{ currentPage: number; search: string }>();
  collaboratorIdsChanged = output<Option[]>();

  backUrl = input<string>('');
  actionsTemplate = input<TemplateRef<unknown> | null>(null);

  lookupList = signal<Option[]>([]); // All users
  addedList = signal<Option[]>([]); // Added users
  removedList = signal<Option[]>([]); // Removed users
  currentPage = signal<number>(1);
  selectedList = signal<any[]>([]);
  isLookupLoading = signal<boolean>(false);
  totalLookupPages = signal<number>(0);
  lookupListTotalItems = signal<number>(0);
  selectedCollaboratorId = signal<number>(0);
  isDeleteCollaboratorLoading = signal<boolean>(false);
  isAddCollaboratorLoading = signal<boolean>(false);

  appPermissions = AppPermissions;
  requestTypeEnum = RequestTypeEnum;
  isTriggerMenuOpen = signal<boolean>(false);
  generateUrl = computed(() => {
    const origin = window.location.origin;
    const url = `${EMPLOYEE_ROUTES.MY_INBOX.REQUEST}/view/${this.requestDetailsResponse().data.id}`;
    const path = this.requestService.navigationPath(url);
    return `${origin}${path}`;
  });

  triggerRequest(id: number) {
    this.isTriggerMenuOpen.set(false);
    const url = `${EMPLOYEE_ROUTES.REQUESTS.DETAILS}`;
    const path = this.requestService.navigationPath(url);
    this.router.navigate([path, id, RequestTypeEnum.TRIGGER]);
  }

  openScheduleWorkflow(id: number) {
    this.isTriggerMenuOpen.set(false);
    this.router.navigate([
      this.tenantService.tenantPath(`${EMPLOYEE_ROUTES.REQUESTS.DETAILS}`),
      id,
      EMPLOYEE_ROUTES.REQUESTS.SCHEDULE,
    ]);
  }

  toggleTriggerMenu() {
    this.isTriggerMenuOpen.update(value => !value);
  }

  copyLinkCondition = computed(() => {
    return (
      this.requestType() !== this.requestTypeEnum.TRIGGER &&
      this.requestDetailsResponse().data.request_id
    );
  });

  copyLinkTitle = computed(() => {
    return this.requestDetailsResponse().data.request_id
      ? `${this.requestDetailsResponse().data.name}: ${this.requestDetailsResponse().data.request_id}`
      : this.requestDetailsResponse().data.name;
  });

  goBack() {
    const previousParams = this.previousRouteService.getPreviousParams();
    if (this.backUrl()) {
      this.router.navigate([this.backUrl()], {
        queryParams: previousParams || {},
      });
    } else {
      const source = this.activatedRoute.snapshot.queryParams['source'];
      if (this.disableExpand()) {
        this.discardChanges.emit();
      } else if (source) {
        this.router.navigate([this.tenantService.tenantPath('')], {
          queryParams: {},
        });
      } else {
        history.back();
      }
    }
  }

  openAssignCollaboratorsDialog() {
    this.currentPage.set(1);
    this.selectedList.set([]);
    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      this.addedList.set(this.assignedList());
    }
    this.getUsersLookup();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignCollaboratorsDialog(),
      canNotCloseFromOutside: true,
      width: '51',
    });
  }

  getUsersLookup(name = '', page = 1) {
    this.currentPage.set(page);
    this.isLookupLoading.set(true);
    const lookupFilters = {
      name,
      page: this.currentPage(),
      filtersNotState: 'inactive',
      filtersNotStateCancelled: 'cancelled',
      filtersNotCollaboratedForFlow:
        this.requestDetailsResponse().data.id.toString(),
      perPage: 25,
    };

    this.lookupService
      .userLookup(lookupFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.handleUsersLookupResponse(response, name),
        error: () => this.isLookupLoading.set(false),
      });
  }

  handleUsersLookupResponse(
    response: { data: Option[]; meta: Meta },
    name: string
  ) {
    if (this.currentPage() === 1) {
      this.lookupList.set(response.data);
    } else {
      this.lookupList().push(...response.data);
    }

    // remove selected users from the users lookup list
    const removedSelectedListFromLookupList =
      this.collaboratorService.removeSelectedListFromLookupList(
        this.selectedList(),
        this.lookupList()
      );
    this.lookupList.set(removedSelectedListFromLookupList);

    // remove added users from the users lookup list
    const removedAddedListFromLookupList =
      this.collaboratorService.removeAddedListFromLookupList(
        this.addedList(),
        this.lookupList()
      );
    this.lookupList.set(removedAddedListFromLookupList);

    // add removed users to the users lookup list
    const addedRemovedListFromLookupList =
      this.collaboratorService.addRemovedListFromLookupList(
        name,
        this.removedList(),
        this.lookupList()
      );
    this.lookupList.set(addedRemovedListFromLookupList);

    this.totalLookupPages.set(response.meta.last_page);
    this.lookupListTotalItems.set(response.meta.total);
    this.isLookupLoading.set(false);
  }

  onUpdateLookupList(event: { currentPage: number; search: string }) {
    this.selectedList.set([]);
    this.addedList.set(
      this.requestType() === this.requestTypeEnum.TRIGGER
        ? this.assignedList()
        : []
    );
    this.removedList.set([]);
    this.getUsersLookup(event.search, event.currentPage);
  }

  onAddCollaboratorInline(user: Option) {
    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      const normalizedUser: Option = {
        value: String(user.id ?? user.value),
        label: (user as any).name ?? user.label,
        image: user.image,
        job_title: user.job_title,
      };
      const updatedList = [...this.assignedList(), normalizedUser];
      this.collaboratorIdsChanged.emit(updatedList);
      this.lookupList.update(prev =>
        prev.filter(u => u.value !== normalizedUser.value)
      );
      return;
    }

    this.isAddCollaboratorLoading.set(true);
    this.collaboratorService
      .addCollaborators(this.requestDetailsResponse().data.id.toString(), {
        added_user_ids: [Number(user.id ?? user.value)],
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isAddCollaboratorLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.getAssignedCardList.emit({ currentPage: 1, search: '' });
          this.lookupList.update(prev =>
            prev.filter(u => u.value !== (user.id ?? user.value))
          );
        },
      });
  }

  selectItem(item: Option) {
    item.isSelected = true;
    this.selectedList().unshift(item);

    // remove the selected item from the lookup list to avoid duplication
    this.lookupList.set(
      this.lookupList().filter(card => card.value !== item.value)
    );

    if (
      this.currentPage() < this.totalLookupPages() &&
      this.lookupList().length < 7
    ) {
      this.getUsersLookup('', this.currentPage() + 1);
    }
  }

  removeSelectedItem(item: Option) {
    item.isSelected = false;
    this.selectedList.set(
      this.selectedList().filter(card => card.value !== item.value)
    );

    this.lookupList().unshift(item);
  }

  saveAssignedItems() {
    const selectedIds = this.selectedList().map(item => item.value);
    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      const updatedList = [...this.assignedList(), ...this.selectedList()];
      this.collaboratorIdsChanged.emit(updatedList);
      this.modalService.modalContent.set({ isOpen: false });
      return;
    }

    this.isAddCollaboratorLoading.set(true);
    const collaborators = {
      added_user_ids: selectedIds,
    };
    this.collaboratorService
      .addCollaborators(
        this.requestDetailsResponse().data.id.toString(),
        collaborators
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isAddCollaboratorLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.getAssignedCardList.emit({ currentPage: 1, search: '' });
          this.modalService.modalContent.set({
            isOpen: false,
          });
        },
      });
  }

  onDeleteCollaborator(collaboratorId: number | string) {
    if (this.isRequestHeaderUiEnhancementEnabled()) {
      this.deleteCollaboratorDirectly(Number(collaboratorId));
    } else {
      this.selectedCollaboratorId.set(Number(collaboratorId));
      this.modalService.modalContent.set({
        isOpen: true,
        customizeContent: this.deleteCollaboratorDialog(),
        width: '24',
      });
    }
  }

  private deleteCollaboratorDirectly(id: number) {
    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      const removedUser = this.assignedList().find(
        item => Number(item.value) === id
      );
      const remaining = this.assignedList().filter(
        item => Number(item.value) !== id
      );
      this.collaboratorIdsChanged.emit(remaining);
      if (removedUser) {
        this.lookupList.update(prev => [removedUser, ...prev]);
      }
      return;
    }

    this.collaboratorService
      .removeCollaborators(this.requestDetailsResponse().data.id.toString(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.getAssignedCardList.emit({ currentPage: 1, search: '' });
        },
      });
  }

  closeDeleteCollaboratorDialog() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  deleteCollaborator() {
    const id = this.selectedCollaboratorId();
    this.closeDeleteCollaboratorDialog();
    this.selectedCollaboratorId.set(0);

    if (this.requestType() === this.requestTypeEnum.TRIGGER) {
      const remaining = this.assignedList().filter(
        item => Number(item.value) !== id
      );
      this.collaboratorIdsChanged.emit(remaining);
      return;
    }

    this.isDeleteCollaboratorLoading.set(true);
    this.collaboratorService
      .removeCollaborators(this.requestDetailsResponse().data.id.toString(), id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isDeleteCollaboratorLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.getAssignedCardList.emit({ currentPage: 1, search: '' });
        },
      });
  }
}
